// frontend/src/services/etaService.js
import axios from '../axiosConfig';
import WebSocketService from './WebSocketService';

class ETAService {
    captureResponseOwner(tripId, currentEntity) {
        const clientKey = `entity_${currentEntity.entityId}_trip_${tripId}`;
        const admission = WebSocketService.getEtaAdmission(clientKey);
        // HTTP bootstrap is authorized by this response and scoped to the
        // connection/request generation captured before starting the request.
        return payload => WebSocketService.storeLatestEta(clientKey, payload, Date.now(), {
            admission, bootstrap: true
        });
    }
    /**
     * Initialize ETAs when trip starts
     */
    async initializeETA(tripId, currentLocation, currentEntity) {
        const acceptResponse = this.captureResponseOwner(tripId, currentEntity);
        console.log('Initializing ETA for trip', { 
            tripId, 
            hasLocation: !!currentLocation,
            entityId: currentEntity?.entityId || currentEntity?.id
        });

        try {
            const response = await axios.post(
                `/api/trips/${currentEntity.entityId}/${tripId}/eta/initialize`,
                { 
                    currentLocation
                }
            );
            console.log('ETA initialization response received', { 
                tripId,
                hasData: !!response.data.data 
            });
            return acceptResponse(response.data.data);
        } catch (error) {
            console.error('Error initializing ETA', error, {
                tripId,
                hasLocation: !!currentLocation,
                entityId: currentEntity?.entityId || currentEntity?.id
            });
            throw error;
        }
    }

    /**
     * Update ETAs based on GPS location
     */
    async updateETA(tripId, currentLocation, currentEntity) {
        const acceptResponse = this.captureResponseOwner(tripId, currentEntity);
        console.log('Updating ETA for trip', { 
            tripId, 
            hasLocation: !!currentLocation,
            entityId: currentEntity?.id 
        });
        // Return early if no GPS data
        if (!currentLocation) {
            console.warn('GPS location not available for ETA update', { tripId });
            return null;
        }

        try {
            const response = await axios.post(
                `/api/trips/${currentEntity.entityId}/${tripId}/eta/update`,
                {
                    currentLocation,
                    timestamp: new Date().toISOString()
                }
            );
            return acceptResponse(response.data.data);
        } catch (error) {
            console.error('Error updating ETA', error, {
                tripId,
                hasLocation: !!currentLocation,
                entityId: currentEntity?.id
            });
            throw error;
        }
    }

    /**
     * Force refresh ETAs (admin/driver only)
     */
    async forceRefreshETA(tripId, currentLocation, currentEntity) {
        const acceptResponse = this.captureResponseOwner(tripId, currentEntity);
        // Return early if no GPS data
        if (!currentLocation) {
            console.warn('GPS location not available for ETA refresh', { tripId });
            return null;
        }

        try {
            const response = await axios.post(
                `/api/trips/${currentEntity.entityId}/${tripId}/eta/refresh`,
                { 
                    currentLocation
                }
            );
            return acceptResponse(response.data.data);
        } catch (error) {
            console.error('Error force refreshing ETA', error, {
                tripId,
                hasLocation: !!currentLocation,
                entityId: currentEntity?.id
            });
            throw error;
        }
    }

    /**
     * Get current ETA data from backend (all user roles)
     * Used when we need fresh backend data but don't have GPS or can't force refresh
     */
    async getCurrentETA(tripId, currentEntity, tripData, currentLocation = null) {
        const acceptResponse = this.captureResponseOwner(tripId, currentEntity);
        try {
            const params = {
                route_id: tripData.route_id,
                departure_time: tripData.departure_time,
                trip_date: tripData.trip_date,
                status: tripData.status
            };

            // Include current location if available
            if (currentLocation) {
                params.current_lat = currentLocation.lat;
                params.current_lng = currentLocation.lng;
            }

            const response = await axios.get(
                `/api/trips/${currentEntity.entityId}/${tripId}/eta`,
                { params }
            );
            return acceptResponse(response.data.data);
        } catch (error) {
            console.error('Error getting current ETA', error, { tripId });
            throw error;
        }
    }
}

export default new ETAService();
